import React, { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import useDashboardService from "../services/useDashboardService";
import { EventSubtypes, EventTypes, kSrvMsgIdentifier, useSocketContext } from "./SocketContext";
import { useTranslation } from "react-i18next";
import useToast, { ToastPosition, ToastType } from "../hooks/useToast";
import { DashboardData } from "../types/Dashboard";

const DashboardContext = createContext<{
    data: DashboardData | null;
    loading: boolean;
}>({
    data: null,
    loading: true,
});

export const DashboardProvider = ({ children }: { children: ReactNode }) => {
    const [data, setData] = useState<DashboardData | null>(null);
    const [loading, setLoading] = useState(true);
    const firstCallRef = useRef(true);
    const setFirstCall = (val: boolean) => { firstCallRef.current = val; };

    const { fetchDashboardData } = useDashboardService();
    const { socket } = useSocketContext();
    const { t } = useTranslation("common");
    const { showToast } = useToast();

    const handleFetchDashboardData = useCallback(async () => {
        if (firstCallRef.current) {
            setLoading(true);
        }

        fetchDashboardData().then((data) => {
            setData(data);
        })
            .catch(() => {
                showToast({
                    type: ToastType.ERROR,
                    message: t("common.unknownError"),
                    position: ToastPosition.TOP_CENTER,
                });
            })
            .finally(() => {
                if (firstCallRef.current) {
                    setFirstCall(false);
                    setLoading(false);
                }
            });
    }, [fetchDashboardData, showToast, t]);

    useEffect(() => {
        handleFetchDashboardData();

        const refetchInterval = setInterval(() => {
            handleFetchDashboardData();
        }, 5 * 60 * 1000);

        socket?.on(kSrvMsgIdentifier, (data: { type: string; subtype: string; }) => {
            if (data.type === EventTypes.LIVE_EVENT && (
                data.subtype === EventSubtypes.ANPR_ENTRY ||
                data.subtype === EventSubtypes.CHECK_IN ||
                data.subtype === EventSubtypes.CHECK_OUT
            )) {
                handleFetchDashboardData();
            }
        });

        return () => {
            clearInterval(refetchInterval);
        }
    }, [handleFetchDashboardData, socket]);

    return <DashboardContext.Provider
        value={{ data, loading }}
    >
        {children}
    </DashboardContext.Provider>;
};

export const useDashboard = () => {
    const context = useContext(DashboardContext);
    if (!context) {
        throw new Error("useDashboard must be used within a DashboardProvider");
    }
    return context;
};
